/**
 * 서명 목록 카드용 미리보기 HTML.
 *
 * <h3>왜 원본을 그대로 못 쓰나</h3>
 * <p>목록 카드는 "이게 어느 서명인지" 를 한눈에 알아보라고 있는 자리다. 그런데 외부 편집기
 * (DEXT5·아웃룩 등)에서 옮겨온 서명은 본문과의 간격용으로 앞뒤에 {@code <p><br></p>} 를
 * 열 개씩 달고 있는 일이 흔하다. 카드의 좁은 높이 안에서는 그 빈 줄만 보이다 잘려서
 * <b>카드가 통째로 흰 화면</b>이 된다. 그래서 미리보기에서만 앞뒤 빈 줄을 걷어낸다.
 *
 * <h3>건드리지 않는 것</h3>
 * <ul>
 *   <li>저장된 서명 본문 — 그 빈 줄은 본문과 서명 사이 여백으로 쓰이는 의도된 공백이다.</li>
 *   <li>편집기(SignatureEditorDialog)의 미리보기 — 거기는 "저장하면 이대로 박힌다" 를 보여
 *       주는 자리라 빈 줄도 있는 그대로 보여야 한다.</li>
 * </ul>
 *
 * <p>{@link pinImageBoxes} 를 통과시키는 이유는 작성기·편집기 미리보기와 같다 — 로고가 안 떠도
 * 열 폭이 유지돼야 명함형 서명이 접히지 않는다.
 */
import DOMPurify from "dompurify";

import { pinImageBoxes } from "./pinImageBoxes";

/** 이 태그가 들어 있으면 글자가 없어도 "볼 것이 있는" 블록이다. */
const VISUAL = "img, hr, table, video, iframe, svg";

/** &nbsp; 를 보통 공백으로 바꾼 뒤 남는 글자가 있는지. */
const hasText = (node) => Boolean((node.textContent || "").replace(/ /g, " ").trim());

/** 화면에 아무것도 안 남기는 노드인가 (빈 단락, 홑 br, 공백 텍스트, 주석). */
function isBlank(node) {
    if (node.nodeType === Node.TEXT_NODE) return !hasText(node);
    if (node.nodeType !== Node.ELEMENT_NODE) return true;   // 주석 등
    if (node.tagName === "BR") return true;
    if (node.querySelector(VISUAL)) return false;
    return !hasText(node);
}

/**
 * 앞뒤의 빈 블록을 걷어낸다. 가운데 빈 줄은 그대로 둔다 — 이름과 연락처 사이 간격처럼
 * 의미가 있는 여백일 수 있어서.
 *
 * <p>DOMParser 로 파싱하는 이유는 이 폴더의 다른 모듈들과 같다: 살아있는 DOM 에 넣으면
 * 죽은 URL 로 요청이 나가고 {@code onerror} 가 실행될 수 있다. DOMParser 문서는 비활성이다.
 */
export function trimBlankEdges(html) {
    if (!html) return html;
    const doc  = new DOMParser().parseFromString(String(html), "text/html");
    const body = doc.body;

    while (body.firstChild && isBlank(body.firstChild)) body.firstChild.remove();
    while (body.lastChild  && isBlank(body.lastChild))  body.lastChild.remove();

    return body.innerHTML;
}

/**
 * 목록 카드에 바로 꽂을 수 있는 HTML.
 * 백엔드가 이미 새니타이즈하지만, HTML 직접 입력을 허용한 이상 렌더 직전 한 번 더 거른다.
 *
 * @param {string} bodyHtml 저장된 서명 본문
 * @returns {string} 빈 줄을 걷어내고 정제한 HTML. 내용이 없으면 빈 문자열.
 */
export function toSignaturePreviewHtml(bodyHtml) {
    const trimmed = trimBlankEdges(bodyHtml);
    if (!trimmed || !trimmed.trim()) return "";
    return DOMPurify.sanitize(pinImageBoxes(trimmed), { ADD_ATTR: ["target", "rel"] });
}
